import React, {useCallback, useMemo} from 'react';
import {Typography} from '@mui/material';
import {positionHighlightLabelInFrame} from './highlightLabelPositioning';

// Html segment renderer (used in HTML view mode)
const HtmlSegmentView = React.memo(({
                                        index,
                                        rawHtml,
                                        entities = [],
                                        onEntityClick,
                                        highlightType = null,
                                        highlightLabel = null,
                                    }) => {
    const labelText = (highlightLabel && String(highlightLabel).trim())
        ? String(highlightLabel)
        : (highlightType === 'cited' ? 'Cited' : (highlightType === 'sourcePackage' ? 'In package' : ''));

    const highlightedHtml = useMemo(() => {
        try {
            return rawHtml || '';
        } catch (e) {
            console.error('Failed to build highlighted HTML', e);
            return rawHtml || '';
        }
    }, [rawHtml]);

    const handleClick = useCallback((e) => {
        const target = e.target;
        if (!target) return;

        // Best-effort: support wikidata mentions if present
        const mentionEl = target.closest?.('.wikidata-mention');
        if (mentionEl && onEntityClick) {
            const entityId = mentionEl.getAttribute('data-entity-id') || mentionEl.getAttribute('data-wikidata-id');
            const entity = entities.find(en => (en?.id || en?.entityId || en?.wikidataId) === entityId);
            if (entity) onEntityClick(entity, mentionEl);
        }
    }, [entities, onEntityClick]);

    const handleEnterOrFocus = useCallback((e) => {
        if (!highlightType) return;
        const hl = e.currentTarget;
        positionHighlightLabelInFrame(hl, hl);
    }, [highlightType]);

    return (
        <div
            id={`segment-${index}`}
            className={highlightType ? `hl hl-${highlightType}` : undefined}
            data-label={labelText}
            onMouseEnter={handleEnterOrFocus}
            onFocus={handleEnterOrFocus}
            onClick={handleClick}
            tabIndex={highlightType ? 0 : undefined}
            style={{padding: '8px', margin: '4px 0'}}
        >
            {highlightType && labelText ? (
                <span className="hl-label" role="tooltip">{labelText}</span>
            ) : null}

            {highlightedHtml ? (
                <div
                    className="html-segment-content"
                    style={{lineHeight: '1.6', wordBreak: 'break-word'}}
                    dangerouslySetInnerHTML={{__html: highlightedHtml}}
                />
            ) : (
                <Typography variant={'body2'} color={'text.secondary'}>
                    [No HTML content]
                </Typography>
            )}
        </div>
    );
});

export default HtmlSegmentView;

